/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { FormControlProps, InputLabelProps, TextField, Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps, TextFieldProps, InputAdornment } from '@mui/material';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { IFieldInputProps, useFormErrorBoundary } from '@ngt/forms-core';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { IAdditionalFormInputProps } from './Input';
import SelectIcon from './SelectIcon';
import { FormError } from '../../../errors';
import FormErrorType from '@/types/form/FormErrorType';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */


type AutocompleteMultipleProps<TId = any> = Partial<Omit<MuiAutocompleteProps<any, true, true, true>, 'options'>> & IFieldInputProps<TId[], any, FormError, TId[]> & IAdditionalFormInputProps

export interface IAutocompleteMultipleItem<TId = any> {
    id: TId,
    value: string
}

export interface IAutocompleteMultipleProps<TId = any> extends AutocompleteMultipleProps<TId> {
    TextFieldProps?: TextFieldProps;
    options?: IAutocompleteMultipleItem<TId>[];
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const AutocompleteMultiple = <TId = any>({
    FieldInputProps: { state: { value }, actions: { update, blur, focus, touch } },
    label,
    fullWidth,
    TextFieldProps,
    hasErrors,
    errors,
    errorType,
    options,
    ...rest
}: IAutocompleteMultipleProps<TId>) => {

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    const optionsToUse = useMemo(() => {
        return options ?? [];
    }, [options]);

    const valueToUse = useMemo(() => {
        if (!value) {
            return [];
        }

        return value.map(x => optionsToUse.find(y => x == y.id)).filter(x => !!x);
    }, [optionsToUse, value])

    return (
        <MuiAutocomplete
            {...rest as any}
            options={optionsToUse}
            getOptionLabel={(option: IAutocompleteMultipleItem<TId>) => option.value}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            value={valueToUse}
            multiple
            popupIcon={<SelectIcon />}
            onChange={(event, newInputValue: IAutocompleteMultipleItem<TId>[]) => {

                update(newInputValue ? newInputValue?.map(x => x.id) : undefined as any, true, true, true);
            }}
            renderInput={(params) => {
                const onFocusCombined = (event: React.FocusEvent<HTMLInputElement>) => {
                    if (params.inputProps.onFocus) {
                        params.inputProps.onFocus(event);
                    }

                    focus();
                };

                const onBlurCombined = (event: React.FocusEvent<HTMLInputElement>) => {
                    if (params.inputProps.onBlur) {
                        params.inputProps.onBlur(event);
                    }

                    blur();
                    touch();
                };

                return (
                    <TextField
                        {...TextFieldProps}
                        {...params}
                        label={TextFieldProps?.label ?? label}
                        InputLabelProps={{
                            ...TextFieldProps?.InputLabelProps,
                            ...params.InputLabelProps
                        }}
                        InputProps={{
                            ...TextFieldProps?.InputProps,
                            ...params.InputProps
                        }}
                        inputProps={{
                            ...TextFieldProps?.inputProps,
                            ...params.inputProps,
                            onBlur: onBlurCombined,
                            onFocus: onFocusCombined
                        }}
                        sx={{...TextFieldProps?.sx, ...errorStyling}}
                        error={hasErrors}
                        helperText={
                            (hasErrors || TextFieldProps?.helperText) && (
                                <>
                                    {TextFieldProps?.helperText && (<span className={ErrorClassMapping[FormErrorType.HelperText]}>{TextFieldProps.helperText}</span>)} 
                                    {errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
                                </>
                            )
                        }
                    />
                )
            }}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default AutocompleteMultiple;
